import { useEffect, useRef } from 'react';

/**
 * Attaches a ResizeObserver to the footer element and keeps the
 * `--footer-height` CSS custom property on `:root` in sync with its height.
 *
 * This is so that the salesforce chat icon can adjust positioning based on the footer height and avoid being covered by it.
 */
const useObserverFooterHeight = () => {
  const footerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const footer = footerRef.current;
    if (!footer) return;

    const updateFooterHeight = () => {
      const height = footer.getBoundingClientRect().height;
      // set the CSS variable on the root element so it can be used globally (e.g. in App.css for the salesforce chat icon)
      document.documentElement.style.setProperty('--footer-height', `${height}px`);
    };

    // Set the initial value immediately after mount.
    updateFooterHeight();

    // Re-calculate whenever the footer's dimensions change.
    const resizeObserver = new ResizeObserver(updateFooterHeight);
    resizeObserver.observe(footer);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return footerRef;
};

export default useObserverFooterHeight;
